import { ThunkActionType } from "../actions/types";
import { getSettingBoolean } from "../../models/utilities/settingUtilities";
import { StoreState } from "../../models/states/StoreState";
import { setProbeStatus, setProbingLoading, setVariableMentions } from "../actions";
import { generateProbedQuestionsSequence } from "./probedQuestions";
import { removeHtmlTags } from "../../utils";
import { translate } from "../../models/utilities/translationUtilities";
import { QuestionType } from "../../models/enums/QuestionType";
import { API_HAS_FAILED } from "../../services/AIProbeService";
import { ProbedQuestionModel } from "../../models/ProbedQuestionModel";

export const DISABLE_PROBING = process.env.NODE_ENV === 'test';

/**
 * Check if any questions on the current page need probing
 */
export const checkIfProbingNeeded = (): ThunkActionType<Promise<boolean>> => {
    return async (dispatch, getState) => {
        // If API has failed previously, no questions need probing
        if (API_HAS_FAILED) {
            console.log('API previously failed, skipping all probing checks');
            dispatch(setProbeStatus(false, []));
            return false;
        }
        
        const state: StoreState = getState();
        const { interview } = state;
        const { navigation, variables } = interview;
        const { currentPage } = navigation;
        const { questions } = currentPage;
        
        const questionsThatNeedProbing = questions.filter(question => {
            if (question.type !== QuestionType.TextAnswer && 
                question.type !== QuestionType.OpenEndAnswer) {
                return false;
            }
            
            const enabledAIProbing = getSettingBoolean(question, "EnableAiProbeContainer");
            if (!enabledAIProbing) {
                return false;
            }
            
            const [mainVariable] = question.variables;
            if (!mainVariable) {
                return false;
            }
            
            const mentions = variables.mentionsByVariableName[mainVariable.name];
            if (!mentions || mentions.length === 0) {
                return false;
            }
            
            const existingProbes = variables.probedQuestions[mainVariable.elementId] || [];

            if (existingProbes.length > 0) {
                const lastProbe = existingProbes[existingProbes.length - 1];
                if (!lastProbe.answer || lastProbe.answer.trim() === '') {
                    return false;
                }
                
                if (lastProbe.shouldContinue === false) {
                    // If shouldContinue is false, don't probe further
                    return false;
                }
            }
            
            return true;
        });
        
        if (questionsThatNeedProbing.length > 0) {
            const questionIds = questionsThatNeedProbing.map(q => q.id.toString());
            dispatch(setProbeStatus(true, questionIds));
            return true;
        } else {
            dispatch(setProbeStatus(false, []));
            return false;
        }
    };
};

/**
 * Generate probes for questions that need them
 */
export const generateProbesForPendingQuestions = (): ThunkActionType<Promise<boolean>> => {
    return async (dispatch, getState) => {
        // If API has failed previously, skip generating probes
        if (API_HAS_FAILED) {
            console.log('API previously failed, skipping probe generation');
            dispatch(setProbeStatus(false, []));
            return false;
        }
        
        const state: StoreState = getState();
        const { interview } = state;
        const { variables, navigation } = interview;
        const { probingStatus } = variables;
        const { currentPage } = navigation;
        const { questions } = currentPage;
        
        const language = interview.respondent?.language || 'en';
        
        if (!probingStatus.active || probingStatus.questionIdsNeedingProbes.length === 0) {
            return false;
        }
        
        const questionsToProbe = questions.filter(q => 
            probingStatus.questionIdsNeedingProbes.includes(q.id.toString()));
        
        const probePromises = questionsToProbe.map(question => {
            const [mainVariable] = question.variables;
            if (!mainVariable) {
                return Promise.resolve(false);
            }
            
            const mentions = variables.mentionsByVariableName[mainVariable.name];
            if (!mentions || mentions.length === 0) {
                return Promise.resolve(false);
            }
            
            // For OpenEndAnswer, the answer is in the openEnd property
            // For regular TextAnswer, the answer is in the value property
            let answerText = '';
            if (question.type === QuestionType.OpenEndAnswer) {
                answerText = mentions[0].openEnd || '';
            } else {
                answerText = mentions[0].value || '';
            }
            
            let originalQuestionText = translate(question, "TEXT", language);
            if (originalQuestionText) {
                originalQuestionText = removeHtmlTags(originalQuestionText);
            }
            
            const EnabledAiProbeLeadRespondend = getSettingBoolean(question, "EnabledAiProbeLeadRespondend");
            const EnabledAiProbeUnengage = getSettingBoolean(question, "EnabledAiProbeUnengage");
            const AiProbeQuestionnaireContext = question.settings["AiProbeQuestionnaireContext"] as string;
            
            // Get existing probed questions and determine if we need to append
            const existingProbes = variables.probedQuestions[mainVariable.elementId] || [];
            const nextOrder = existingProbes.length > 0 ? 
                Math.max(...existingProbes.map(p => p.order)) + 1 : 1;
            
            // Build conversation history for context
            // Include all previous Q&A pairs to build a conversation chain
            const previousQA = existingProbes
                .filter(q => q.answer && q.answer.trim() !== '')
                .map(q => ({
                    question: q.questionText,
                    answer: q.answer
                }));

            // Enhanced context with conversation history
            const context = {
                questionText: originalQuestionText,
                dontlead: !EnabledAiProbeLeadRespondend,
                unengaged: !EnabledAiProbeUnengage,
                usercontext: AiProbeQuestionnaireContext || "We need to know their thoughts on this topic",
                questionType: question.type,
                previousQA: previousQA, // Add the conversation history
                lastProbeOrder: existingProbes.length > 0 ? existingProbes[existingProbes.length - 1].order : 0
            };
            
            // Generate next probed question(s) with the next order number
            return dispatch(generateProbedQuestionsSequence(
                // If we have previous probes, use the last probe's answer as the answer text
                previousQA.length > 0 ? previousQA[previousQA.length - 1].answer : answerText,
                mainVariable.elementId,
                mainVariable.elementId,
                mainVariable.name,
                question.id.toString(),
                context,
                undefined,
                nextOrder, // Use the next order number instead of starting from 1
                question.settings
            ));
        });
        
        try {
            await Promise.all(probePromises);
        } catch (error) {
            console.error('Error during probe generation:', error);
            // Set the API failure flag in case it wasn't set already
            if (typeof window !== 'undefined') {
                (window as any).API_HAS_FAILED = true;
            }
            // Allow navigation to continue
            dispatch(setProbeStatus(false, []));
            return false;
        }
        
        dispatch(setProbeStatus(false, []));
        return true;
    };
};

/**
 * Handle probing before navigation
 * @returns true if navigation should continue, false if blocked
 */
export const handleProbingBeforeNavigation = (): ThunkActionType<Promise<boolean>> => {
    return async (dispatch, getState) => {
        if (DISABLE_PROBING || API_HAS_FAILED) {
            return true;
        }
        
        dispatch(setProbingLoading(true));
        
        try {
            // First, set variable mentions for all answered probed questions
            // This is where we sync the probed question answers with the variable mentions
            const state: StoreState = getState();
            const { interview } = state;
            const { variables } = interview;
            const { probedQuestions } = variables;
            
            // For each main variable that has probed questions
            Object.entries(probedQuestions).forEach(([mainVariableId, questions]) => {
                // First find the original question to determine its type
                const originalQuestion = state.interview.navigation.currentPage.questions.find(q => {
                    const [variable] = q.variables;
                    return variable && variable.elementId === mainVariableId;
                });
            
                // Get the main variable name to use in the consolidated variable
                const [mainVariable] = originalQuestion?.variables || [];
                const mainVariableName = mainVariable?.name || mainVariableId;
                
                // Create the variable names for all probes - both legacy and new split approach
                const legacyProbeVariableName = `${mainVariableName}_AI_PROBING`;
                const probeQuestionsVariableName = `${mainVariableName}_AI_PROBING_QUESTIONS`;
                const probeAnswersVariableName = `${mainVariableName}_AI_PROBING_ANSWERS`;
                
                // Sort answered questions by order
                const answeredQuestions = questions
                    .filter(q => q.answer && q.answer.trim() !== '')
                    .sort((a, b) => a.order - b.order);
                
                // Group questions by matrix
                const questionsByMatrix = answeredQuestions.reduce((groups: {[key: string]: ProbedQuestionModel[]}, question) => {
                    const matrixKey = question.matrix || "default";
                    if (!groups[matrixKey]) {
                        groups[matrixKey] = [];
                    }
                    groups[matrixKey].push(question);
                    return groups;
                }, {});
                
                // For each matrix group, create separate mentions
                Object.entries(questionsByMatrix).forEach(([matrixKey, matrixQuestions]) => {
                    // Set matrix index if it's not the default
                    const matrixIndex = matrixKey !== "default" ? parseInt(matrixKey, 10) : undefined;
                    
                    // Set all mentions in legacy format for backward compatibility
                    dispatch(setVariableMentions(
                        legacyProbeVariableName,
                        mentions => {
                            // Clear existing mentions first
                            mentions.clear();
                            
                            // Add each answered question as a mention
                            matrixQuestions.forEach(question => {
                                // Both question types use the same approach:
                                // - answer in value field
                                // - question text in openEnd field
                                mentions.add(question.answer, question.questionText);
                            });
                            
                            return mentions;
                        },
                        matrixIndex
                    ));
                    
                    // Set questions in the new QUESTIONS variable
                    dispatch(setVariableMentions(
                        probeQuestionsVariableName,
                        mentions => {
                            // Clear existing mentions first
                            mentions.clear();
                            
                            // Add each question text as a mention
                            matrixQuestions.forEach(question => {
                                // Store the question text in both value and openEnd fields
                                mentions.add(question.questionText, question.questionText);
                            });
                            
                            return mentions;
                        },
                        matrixIndex
                    ));
                    
                    // Set answers in the new ANSWERS variable
                    dispatch(setVariableMentions(
                        probeAnswersVariableName,
                        mentions => {
                            // Clear existing mentions first
                            mentions.clear();
                            
                            // Add each answer as a mention
                            matrixQuestions.forEach(question => {
                                // Store the answer in both value and openEnd fields
                                mentions.add(question.answer, question.answer);
                            });
                            
                            return mentions;
                        },
                        matrixIndex
                    ));
                });
            });
            
            // Now check if any questions need probing
            const probingNeeded = await dispatch(checkIfProbingNeeded());
            
            // After the check, if API has now failed, proceed with navigation
            if (API_HAS_FAILED) {
                dispatch(setProbingLoading(false));
                return true;
            }
            
            if (probingNeeded) {
                // Capture the IDs that were deemed needing probes *before* generation attempts clear this status from the active state.
                const initialStateForIds: StoreState = getState();
                const questionIdsThatWereTargeted = [...initialStateForIds.interview.variables.probingStatus.questionIdsNeedingProbes];

                await dispatch(generateProbesForPendingQuestions());
                
                if (API_HAS_FAILED) {
                    dispatch(setProbingLoading(false));
                    return true;
                }

                const finalState: StoreState = getState();
                const { variables: finalVariables, navigation: finalNavigation } = finalState.interview;
                const { questions: allCurrentPageQuestions } = finalNavigation.currentPage;
                const { probedQuestions: allProbedQuestionsMap } = finalVariables;

                let anActionableProbeFound = false;

                // Use questionIdsThatWereTargeted instead of relying on the (now likely cleared) probingStatus in finalState
                if (questionIdsThatWereTargeted && questionIdsThatWereTargeted.length > 0) {
                    for (const qId of questionIdsThatWereTargeted) {
                        const originalQuestion = allCurrentPageQuestions.find(q => q.id.toString() === qId);
                        if (!originalQuestion || !originalQuestion.variables || originalQuestion.variables.length === 0) {
                            continue;
                        }
                        const mainVarElementId = originalQuestion.variables[0].elementId;
                        const probesForVar = allProbedQuestionsMap[mainVarElementId];

                        if (probesForVar && probesForVar.length > 0) {
                            const latestProbe = probesForVar.reduce((latest, p) => (p.order > latest.order ? p : latest));
                            if (latestProbe.questionText && latestProbe.questionText.trim() !== '') {
                                anActionableProbeFound = true;
                                break; 
                            }
                        }
                    }
                }

                if (!anActionableProbeFound) {
                    dispatch(setProbingLoading(false));
                    // Ensure probing status is marked inactive if we are proceeding because no actionable probes were found.
                    dispatch(setProbeStatus(false, [])); 
                    return true; // Proceed to next page.
                }
                
                // Actionable probe(s) found, block navigation.
                dispatch(setProbingLoading(false));
                return false; // Block navigation
            }
            
            // No probing was needed in the first place.
            dispatch(setProbingLoading(false));
            dispatch(setProbeStatus(false, [])); // Ensure status is cleared.
            return true; // Proceed with navigation
        } catch (error) {
            console.error('Error during probing:', error);
            dispatch(setProbingLoading(false));
            return true; // Continue navigation on error
        }
    };
}; 